import { useState, useEffect, useRef } from "react";
import { Box, Container, Typography, Button, Stack, CircularProgress } from "@mui/material";
import { Fade } from "@mui/material";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import ImpactSection from "./ImpactSection";

const SDG_COLORS = [
  "#E5243B","#DDA63A","#4C9F38","#C5192D","#FF3A21","#26BDE2",
  "#FCC30B","#A21942","#FD6925","#DD1367","#FD9D24","#BF8B2E",
  "#3F7E44","#0A97D9","#56C02B","#00689D","#19486A",
];

const HERO_IMAGES = ["/landingpage.png"];
const API_BASE = "https://www.africabuildtoshelter.com/api";
const LABEL_COLORS = ["#5C3311", "#E5793B", "#4E9E8C", "#DDA63A"];

function getImageUrl(path) {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${API_BASE.replace("/api", "")}${path}`;
}

function useApi(endpoint) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API_BASE}${endpoint}`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [endpoint]);
  return { data, loading, error };
}

function SectionHeader({ label, highlight, suffix }) {
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: { xs: 4, md: 6 } }}>
      <Typography sx={{ fontSize: { xs: "13px", md: "15px" }, color: "#5C3311", fontWeight: 700 }}>→</Typography>
      <Typography sx={{ fontSize: { xs: "14px", md: "16px" }, color: "#1a0a00", fontWeight: 400 }}>
        {label}{" "}
        <Box component="span" sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>
          {highlight}
        </Box>
        {suffix && ` ${suffix}`}
      </Typography>
    </Box>
  );
}

function LoadingBox() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
      <CircularProgress sx={{ color: "#5C3311" }} />
    </Box>
  );
}

const programs = [
  {
    title: "Sustainable Housing",
    subtitle: "Pan-Africa Butterfly",
    sdgColor: "#E5793B",
    imagePosition: "center 20%",
    image: "/panafrica1.png",
    description: "Designing modular, off-grid homes using Stabilized Earth Blocks (SEB) — a high-performance architectural system built to adapt across Africa's diverse climates.",
    modalContent: {
      overview: "The Pan-Africa Butterfly is a high-performance architectural system designed to solve the challenges of modern African living. By combining a rigorous 3600mm structural grid with a signature V-roof, this prototype provides a universal Chassis that remains structurally consistent while performing optimally across the continent's diverse climates.",
      details: [
        { label: "The Concept", text: "Built around a 3600mm structural grid and a signature V-roof, the Pan-Africa Butterfly acts as a universal Chassis — structurally consistent yet optimised for any African climate zone through geometry rather than changes in material." },
        { label: "The Universal Skin", text: "Unlike traditional housing, the Pan-Africa Butterfly uses a single, high-efficiency wall Skin that adapts to any zone through geometry rather than a change in materials. The Foundation layer (2400mm) is built from Solid Stabilized Earth Block (SEB) walls." },
        { label: "Why Stabilized Earth Blocks?", text: "SEBs are compressed from local subsoil and a small amount of stabiliser, making them low-cost, low-carbon, and achievable with community labour." },
        { label: "SDG Alignment", text: "SDG 11 (Sustainable Cities & Communities), SDG 9 (Industry, Innovation & Infrastructure), SDG 13 (Climate Action)" },
      ],
    },
  },
  {
    title: "Clean Energy",
    subtitle: "Biogas Project",
    sdgColor: "#DDA63A",
    imagePosition: "center 30%",
    image: "/biogas.png",
    description: "Implementing biogas and clean energy solutions that give underserved communities reliable, affordable, and sustainable power.",
    modalContent: {
      overview: "Our Biogas Project converts organic waste into clean cooking and lighting energy for rural and peri-urban communities.",
      details: [
        { label: "What We Do", text: "We design and install small-scale biogas digesters that turn food waste, animal manure, and agricultural residues into clean fuel." },
        { label: "Impact", text: "Families reduce indoor air pollution, lower energy costs, and gain energy independence — while the digestate becomes rich organic fertiliser." },
        { label: "Field Work", text: "Our team visited Gulu in January 2026 to explore and document biogas systems in action, gathering learnings to scale across Uganda." },
        { label: "SDG Alignment", text: "SDG 7 (Affordable & Clean Energy), SDG 3 (Good Health), SDG 13 (Climate Action)" },
      ],
    },
  },
  {
    title: "Future Water Solutions",
    subtitle: "Water Purification",
    sdgColor: "#4E9E8C",
    imagePosition: "center 30%",
    image: "/water.jpg",
    description: "Upcoming water purification initiatives expanding access to safe and sustainable water across African communities.",
    modalContent: {
      overview: "BTSA's Water Purification initiative is our next frontier — designing low-cost, community-led systems to deliver clean water where it matters most.",
      details: [
        { label: "Vision", text: "We envision communities that manage their own clean water supply through solar-powered filtration and rainwater harvesting systems." },
        { label: "Approach", text: "Partnering with local engineers and international experts to pilot, test, and scale solutions tailored to each community's geography." },
        { label: "Timeline", text: "Pilot programs are slated to begin in late 2026, with full rollout across target regions by 2027." },
        { label: "SDG Alignment", text: "SDG 6 (Clean Water & Sanitation), SDG 3 (Good Health), SDG 17 (Partnerships)" },
      ],
    },
  },
  {
    title: "Education & Capacity",
    subtitle: "Scholarship Chapter",
    sdgColor: "#5C3311",
    imagePosition: "center 25%",
    image: "/education.jpg",
    isScholarship: true,
    description: "Led by our founder Lewis Bisaso — IUEA Best Leader Award 2021–2025 & Action in Africa scholar — this program empowers African youth through scholarship coaching, CV webinars, and mentorship.",
    modalContent: {
      overview: "The BTSA Scholarship Chapter is a dedicated branch empowering members through collaborative education support, skill development, and professional growth.",
      intro: "This chapter provides a unique opportunity for members to support each other's personal and professional growth through collaborative efforts focused on education and skill development.",
      objectives: [
        { num: "01", title: "Collaborative Contribution for Educational Growth", points: ["Members join efforts to contribute to individual growth through scholarship applications for further studies.", "Fosters a supportive community where knowledge and resources are shared to help members secure educational opportunities."] },
        { num: "02", title: "Development of Soft Skills", points: ["Training on writing effective personal statements, professional CVs, resumes, and scholarship essays.", "Online trainings with experienced speakers from across Africa."] },
        { num: "03", title: "Enhancement of Professional Communication", points: ["Emphasis on improving written and verbal communication skills.", "Helps members adapt and thrive in more professional environments."] },
        { num: "04", title: "Digital Literacy and Personal Branding", points: ["Members learn to effectively use social media for digital marketing and personal branding.", "Equips members with modern tools to promote themselves in today's digital world."] },
      ],
      planOfAction: [
        "Organize online webinars for training at the beginning of each chapter",
        "Keep the group updated daily with new scholarships",
        "Apply at least 1 scholarship per week to ensure consistency",
        "Assist members with challenges in the application process",
        "Ensure activeness of all members",
      ],
      enrollLink: "https://forms.gle/NvF89Ev4FGLrQgkX7",
      manager: "Dulandzev Melvis Berinyuy",
    },
  },
];

const FALLBACK_EVENTS = [
  { id: 1, status: "past", label: "SUMMIT", labelColor: "#5C3311", date: "Jan 22–24, 2026", location: "Virtual (Zoom)", title: "Africa Sustainability & SDGs Day (ASSD) 2026", description: "BTSA members joined this landmark virtual event exploring Africa's Transformation Roadmap and accelerating progress towards the 2030 Agenda and Agenda 2063.", image: "/ssd.jpg" },
  { id: 2, status: "past", label: "CONFERENCE", labelColor: "#E5793B", date: "Jan 2026", location: "Speke Resort Munyonyo, Kampala", title: "GAIME Conference", description: "Our members represented BTSA at the GAIME Conference, engaging in discussions around innovation, technology, and forward-thinking solutions in shelter and infrastructure.", image: "/Game.jpg" },
  { id: 3, status: "past", label: "FIELD VISIT", labelColor: "#4E9E8C", date: "Jan 20–23, 2026", location: "Gulu, Uganda", title: "Biogas Systems Exploration – Gulu", description: "BTSA travelled to Gulu to explore biogas systems as a sustainable energy solution, learning how small changes create big impact for communities and the planet.", image: "/gulu.jpg" },
  { id: 4, status: "past", label: "SIDE EVENT", labelColor: "#DDA63A", date: "Jan 24, 2026", location: "Virtual (Zoom)", title: "BTSA Side Event at ASSD 2026", description: "Seven committed BTSA members presented at the African Summit for Sustainable Development, contributing to key conversations on sustainable housing and Africa's future.", image: "/sideevent.jpg" },
];

function ProgramModal({ program, onClose }) {
  const isScholarship = program.isScholarship;
  const mc = program.modalContent;
  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    <Box onClick={handleBackdrop} sx={{ position: "fixed", inset: 0, zIndex: 1300, bgcolor: "rgba(10,4,0,0.72)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", p: { xs: 2, md: 4 }, animation: "fadeIn 0.22s ease", "@keyframes fadeIn": { from: { opacity: 0 }, to: { opacity: 1 } } }}>
      <Box sx={{ position: "relative", bgcolor: "#FFFFFF", borderRadius: "12px", width: "100%", maxWidth: isScholarship ? "760px" : "680px", maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 32px 80px rgba(0,0,0,0.2)", animation: "slideUp 0.28s cubic-bezier(0.25,0.46,0.45,0.94)", "@keyframes slideUp": { from: { opacity: 0, transform: "translateY(24px)" }, to: { opacity: 1, transform: "translateY(0)" } } }}>
        <Box sx={{ height: { xs: "200px", md: "280px" }, position: "relative", overflow: "hidden", flexShrink: 0 }}>
          <Box component="img" src={program.image} alt={program.title} sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: program.imagePosition ?? "center 30%", display: "block" }} />
          <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.1) 60%)" }} />
          <Box sx={{ position: "absolute", bottom: 20, left: 24, right: 56 }}>
            <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "2px", textTransform: "uppercase", color: program.sdgColor, mb: 0.5 }}>{program.subtitle}</Typography>
            <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: { xs: "22px", md: "28px" }, fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>{program.title}</Typography>
          </Box>
          <Box component="button" onClick={onClose} sx={{ position: "absolute", top: 14, right: 14, width: 32, height: 32, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.15)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: "16px", border: "none", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>✕</Box>
        </Box>
        <Box sx={{ overflowY: "auto", p: { xs: 3, md: 4 } }}>
          <Typography sx={{ fontSize: "14px", color: "rgba(26,10,0,0.65)", lineHeight: 1.8, mb: 3, borderLeft: `3px solid ${program.sdgColor}`, pl: 2, fontStyle: "italic" }}>{mc.overview}</Typography>
          {isScholarship ? (
            <>
              {mc.intro && <Typography sx={{ fontSize: "13.5px", color: "rgba(26,10,0,0.6)", lineHeight: 1.75, mb: 3 }}>{mc.intro}</Typography>}
              <Typography sx={{ fontSize: "11px", fontWeight: 800, letterSpacing: "2px", textTransform: "uppercase", color: "#5C3311", mb: 2 }}>Core Objectives</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mb: 3 }}>
                {mc.objectives.map((obj) => (
                  <Box key={obj.num} sx={{ display: "flex", gap: 2 }}>
                    <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: "28px", fontWeight: 700, color: "rgba(92,51,17,0.15)", lineHeight: 1, flexShrink: 0, mt: "-2px" }}>{obj.num}</Typography>
                    <Box>
                      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1a0a00", mb: 0.75 }}>{obj.title}</Typography>
                      {obj.points.map((p, i) => (
                        <Typography key={i} sx={{ fontSize: "13px", color: "rgba(26,10,0,0.6)", lineHeight: 1.7, mb: 0.5, "&::before": { content: '"›  "', color: program.sdgColor, fontWeight: 700 } }}>{p}</Typography>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
              <Box sx={{ bgcolor: "rgba(92,51,17,0.04)", borderRadius: "8px", p: 2.5, mb: 3 }}>
                <Typography sx={{ fontSize: "11px", fontWeight: 800, letterSpacing: "2px", textTransform: "uppercase", color: "#5C3311", mb: 1.5 }}>Plan of Action</Typography>
                {mc.planOfAction.map((item, i) => (
                  <Box key={i} sx={{ display: "flex", gap: 1.5, mb: 0.75, alignItems: "flex-start" }}>
                    <Box sx={{ width: 18, height: 18, borderRadius: "50%", bgcolor: "#5C3311", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: 700, flexShrink: 0, mt: "1px" }}>{i + 1}</Box>
                    <Typography sx={{ fontSize: "13px", color: "rgba(26,10,0,0.65)", lineHeight: 1.65 }}>{item}</Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, pt: 2.5 }}>
                <Box>
                  <Typography sx={{ fontSize: "11px", color: "rgba(26,10,0,0.4)", letterSpacing: "1px", textTransform: "uppercase" }}>Chapter Manager</Typography>
                  <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1a0a00" }}>{mc.manager}</Typography>
                </Box>
                <Box component="a" href={mc.enrollLink} target="_blank" rel="noopener noreferrer" sx={{ display: "inline-flex", alignItems: "center", gap: 1, px: 3, py: 1.2, bgcolor: "#5C3311", color: "#fff", borderRadius: "6px", fontSize: "12px", fontWeight: 700, textDecoration: "none", "&:hover": { bgcolor: "#DDA63A" } }}>Enroll Now →</Box>
              </Box>
            </>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {mc.details.map((d) => (
                <Box key={d.label} sx={{ pb: 2.5, borderBottom: "1px solid rgba(26,10,0,0.06)", "&:last-child": { borderBottom: "none", pb: 0 } }}>
                  <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "2px", textTransform: "uppercase", color: program.sdgColor, mb: 0.75 }}>{d.label}</Typography>
                  <Typography sx={{ fontSize: "13.5px", color: "rgba(26,10,0,0.65)", lineHeight: 1.75 }}>{d.text}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
function MissionSection() {
  return (
    <Box sx={{ bgcolor: "#FFFFFF", py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: { xs: 6, md: 8 },
          alignItems: "center",
        }}>
          {/* LEFT: Text */}
          <Box>
            <Typography sx={{
              fontSize: "11px", fontWeight: 700, letterSpacing: "2px",
              textTransform: "uppercase", color: "#DDA63A", mb: 2,
            }}>
              Pan-African Mission
            </Typography>
            <Typography sx={{
              fontFamily: "'Playfair Display', serif",
              fontSize: { xs: "32px", sm: "38px", md: "44px" },
              fontWeight: 700, color: "#1a0a00", lineHeight: 1.15, mb: 3,
            }}>
              Building Shelter Across Africa, One Community At A Time
            </Typography>
            <Typography sx={{
              fontSize: "14px", color: "rgba(26,10,0,0.55)",
              lineHeight: 1.8, mb: 4, maxWidth: 420,
            }}>
              Shelter Africa is committed to providing dignified, affordable housing
              for every African family — using locally sourced materials, skilled
              African hands, and sustainable design built for our climate and culture.
            </Typography>
            <Button
              variant="contained"
              href="#programs"
              sx={{
                bgcolor: "#5C3311", color: "#fff", fontWeight: 700,
                fontSize: "12px", letterSpacing: "2px", px: 4, py: 1.6,
                borderRadius: "6px", textTransform: "uppercase",
                boxShadow: "none",
                "&:hover": { bgcolor: "#3e220b" },
              }}
            >
              Join The Movement
            </Button>
          </Box>

          {/* RIGHT: Image collage */}
          <Box sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1.4fr",
            gridTemplateRows: "auto auto",
            gap: "12px",
            height: { xs: "380px", md: "420px" },
          }}>
            {/* Top-left image */}
            <Box sx={{
              borderRadius: "12px", overflow: "hidden",
              gridColumn: "1", gridRow: "1",
            }}>
              <Box
                component="img"
                src="/Game.jpg"
                alt="Mission 1"
                sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top", display: "block" }}
              />
            </Box>

            {/* Bottom-left image */}
            <Box sx={{
              borderRadius: "12px", overflow: "hidden",
              gridColumn: "1", gridRow: "2",
            }}>
              <Box
                component="img"
                src="/values.jpg"
                alt="Mission 2"
                sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }}
              />
            </Box>

            {/* Right tall image — spans both rows */}
            <Box sx={{
              borderRadius: "12px", overflow: "hidden",
              gridColumn: "2", gridRow: "1 / 3",
            }}>
              <Box
                component="img"
                src="/vision.jpg"
                alt="team"
                sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top", display: "block" }}
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

function VideoSection() {
  const sectionRef = useRef(null);
  const [videoSrc, setVideoSrc] = useState(null);
  const [hasAutoPlayed, setHasAutoPlayed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const { data: homeData, loading } = useApi("/home/");
  const apiVideo = homeData?.featured_video ?? null;

  const FALLBACK_VIDEO = {
    title: "Waste Recycling Project at IUEA",
    description: "Watch how our team transformed waste into opportunity at the International University of East Africa — raising awareness, encouraging responsibility, and proving that development goes beyond structures.",
    url: "https://bit.ly/3ZP2eXe",
    embedUrl: "https://www.youtube.com/embed/WYXbDO34Cjk?rel=0&modestbranding=1",
    thumbnail: "https://img.youtube.com/vi/WYXbDO34Cjk/maxresdefault.jpg",
  };

  function buildEmbed(url) {
    if (!url) return null;
    if (url.includes("youtube.com/embed")) return url + "?rel=0&modestbranding=1&autoplay=1";
    const match = url.match(/[?&]v=([^&]+)/);
    if (match) return `https://www.youtube.com/embed/${match[1]}?rel=0&modestbranding=1&autoplay=1`;
    const short = url.match(/youtu\.be\/([^?]+)/);
    if (short) return `https://www.youtube.com/embed/${short[1]}?rel=0&modestbranding=1&autoplay=1`;
    return url;
  }

  const video = apiVideo ?? null;
  const baseEmbed = video ? buildEmbed(video.video_url ?? video.url) : FALLBACK_VIDEO.embedUrl;
  const displayTitle = video?.title ?? FALLBACK_VIDEO.title;
  const displayDescription = video?.description ?? FALLBACK_VIDEO.description;
  const displayUrl = video?.video_url ?? video?.url ?? FALLBACK_VIDEO.url;
  const thumbnail = FALLBACK_VIDEO.thumbnail;
  const firstLetter = displayTitle.charAt(0);
  const restOfTitle = displayTitle.slice(1);

  return (
    <Box ref={sectionRef} sx={{ bgcolor: "#FFFFFF", py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        {loading ? <LoadingBox /> : (
          <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1.3fr" },
            gap: { xs: 6, md: 8 },
            alignItems: "center",
          }}>

            {/* LEFT: Text */}
            <Box>
              <Typography sx={{
                fontFamily: "'Playfair Display', serif",
                fontSize: { xs: "28px", md: "36px" },
                fontWeight: 700,
                color: "#1a0a00",
                lineHeight: 1.25,
                mb: 3,
              }}>
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: { xs: 38, md: 46 },
                    height: { xs: 38, md: 46 },
                    borderRadius: "50%",
                    bgcolor: "#5C3311",
                    color: "#fff",
                    fontFamily: "'Playfair Display', serif",
                    fontSize: { xs: "22px", md: "28px" },
                    fontWeight: 700,
                    mr: 0.5,
                    verticalAlign: "middle",
                    lineHeight: 1,
                  }}
                >
                  {firstLetter}
                </Box>
                {restOfTitle}
              </Typography>

              <Typography sx={{
                fontSize: "15px",
                color: "rgba(26,10,0,0.55)",
                lineHeight: 1.8,
                mb: 4,
                maxWidth: 420,
              }}>
                {displayDescription}
              </Typography>

              {displayUrl && (
                <Box
                  component="a"
                  href={displayUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#1a0a00",
                    textDecoration: "underline",
                    textUnderlineOffset: "3px",
                    transition: "color 0.2s",
                    "&:hover": { color: "#5C3311" },
                  }}
                >
                  View full video
                </Box>
              )}
            </Box>

            {/* RIGHT: Video with brown squares both corners */}
            <Box sx={{ position: "relative", p: "20px" }}>

              {/* Brown square TOP-LEFT */}
              <Box sx={{
                position: "absolute",
                top: 0, left: 0,
                width: "200px", height: "230px",
                bgcolor: "#5C3311",
                borderRadius: "12px",
                zIndex: 0,
              }} />

              {/* Brown square BOTTOM-RIGHT */}
              <Box sx={{
                position: "absolute",
                bottom: 0, right: 0,
                width: "200px", height: "230px",
                bgcolor: "#5C3311",
                borderRadius: "12px",
                zIndex: 0,
              }} />

              {/* Video */}
              <Box
                sx={{
                  position: "relative",
                  zIndex: 1,
                  borderRadius: "16px",
                  overflow: "hidden",
                  aspectRatio: "4/3",
                  bgcolor: "#111",
                  cursor: "pointer",
                }}
                onClick={() => setPlaying(true)}
              >
                {playing ? (
                  <Box
                    component="iframe"
                    src={`${baseEmbed}&autoplay=1`}
                    title={displayTitle}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    sx={{ width: "100%", height: "100%", border: "none", display: "block" }}
                  />
                ) : (
                  <>
                    <Box
                      component="img"
                      src={thumbnail}
                      alt={displayTitle}
                      sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                    <Box sx={{
                      position: "absolute", inset: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <Box sx={{
                        width: 64, height: 64,
                        borderRadius: "50%",
                        bgcolor: "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
                        transition: "transform 0.2s",
                        "&:hover": { transform: "scale(1.1)" },
                      }}>
                        <Box sx={{
                          width: 0, height: 0,
                          borderTop: "11px solid transparent",
                          borderBottom: "11px solid transparent",
                          borderLeft: "20px solid #E5243B",
                          ml: "5px",
                        }} />
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            </Box>

          </Box>
        )}
      </Container>
    </Box>
  );
}

function EventCard({ event, idx, isApi }) {
  const image = isApi ? getImageUrl(event.image) : event.image;
  const isPast = isApi ? event.is_past : event.status === "past";
  const label = isApi ? (event.event_type_display ?? event.event_type ?? "EVENT") : event.label;
  const labelColor = isApi ? LABEL_COLORS[idx % LABEL_COLORS.length] : event.labelColor;
  const date = isApi ? event.date_label : event.date;

  return (
    <Box sx={{ cursor: "default", "&:hover .evt-img": { transform: "scale(1.04)" }, "&:hover .evt-title": { color: "#5C3311" } }}>
      <Box sx={{ overflow: "hidden", borderRadius: "10px", aspectRatio: "4/3", bgcolor: "#f0ede8", position: "relative", mb: 2 }}>
        {image ? (
          <Box component="img" className="evt-img" src={image} alt={event.title} sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 25%", display: "block", transition: "transform 0.6s cubic-bezier(0.25,0.46,0.45,0.94)" }} />
        ) : (
          <Box className="evt-img" sx={{ width: "100%", height: "100%", bgcolor: "#e8e4de" }} />
        )}
        <Box sx={{ position: "absolute", top: 10, left: 10, bgcolor: isPast ? "rgba(92,51,17,0.85)" : "#DDA63A", color: "#fff", fontSize: "9px", fontWeight: 800, letterSpacing: "1.5px", textTransform: "uppercase", px: 1.2, py: 0.5, borderRadius: "4px" }}>
          {isPast ? "Past" : "● Upcoming"}
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "1.5px", textTransform: "uppercase", color: labelColor }}>{label}</Typography>
        <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "rgba(26,10,0,0.2)" }} />
        <Typography sx={{ fontSize: "10px", color: "rgba(26,10,0,0.45)", fontWeight: 500 }}>{date}</Typography>
      </Box>
      <Typography className="evt-title" sx={{ fontFamily: "'Playfair Display', serif", fontSize: { xs: "16px", md: "17px" }, fontWeight: 700, color: "#1a0a00", lineHeight: 1.3, mb: 1, transition: "color 0.2s" }}>{event.title}</Typography>
      {event.location && (
        <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "rgba(26,10,0,0.4)", mb: 1 }}>📍 {event.location}</Typography>
      )}
      <Typography sx={{ fontSize: "13px", color: "rgba(26,10,0,0.55)", lineHeight: 1.65 }}>{event.description}</Typography>
    </Box>
  );
}

function EventsSection() {
  const [filter, setFilter] = useState("all");
  const { data, loading } = useApi(`/events/?filter=${filter}`);
  const apiEvents = data?.events ?? null;
  const events = apiEvents ?? FALLBACK_EVENTS.filter((e) => filter === "all" || e.status === filter);
  const total = data?.total ?? FALLBACK_EVENTS.length;
  const pastCount = data?.past ?? FALLBACK_EVENTS.filter((e) => e.status === "past").length;
  const upcoming = data?.upcoming ?? FALLBACK_EVENTS.filter((e) => e.status === "upcoming").length;

  return (
    <Box sx={{ bgcolor: "#FAFAFA", py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        <Box sx={{ display: "flex", alignItems: { xs: "flex-start", md: "center" }, justifyContent: "space-between", flexDirection: { xs: "column", md: "row" }, gap: 3, mb: { xs: 5, md: 7 } }}>
          <SectionHeader label="Our" highlight="Events" suffix="— on the ground & online" />
          <Box sx={{ display: "flex", gap: 1, mb: { xs: 0, md: 8 } }}>
            {[
              { key: "all", label: `All (${total})` },
              { key: "past", label: `Past (${pastCount})` },
              { key: "upcoming", label: `Upcoming (${upcoming})` },
            ].map((f) => (
              <Box component="button" key={f.key} onClick={() => setFilter(f.key)}
                sx={{ px: 2, py: 0.7, borderRadius: "6px", fontSize: "12px", fontWeight: 700, cursor: "pointer", transition: "all 0.2s", border: "1.5px solid", borderColor: filter === f.key ? "#5C3311" : "rgba(26,10,0,0.15)", bgcolor: filter === f.key ? "#5C3311" : "transparent", color: filter === f.key ? "#fff" : "rgba(26,10,0,0.5)", "&:hover": { borderColor: "#5C3311", color: filter === f.key ? "#fff" : "#5C3311" } }}>
                {f.label}
              </Box>
            ))}
          </Box>
        </Box>
        {loading ? <LoadingBox /> : (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: { xs: 4, md: 3 } }}>
            {events.map((event, idx) => (
              <EventCard key={event.id} event={event} idx={idx} isApi={!!apiEvents} />
            ))}
          </Box>
        )}
        <Box sx={{ mt: { xs: 6, md: 8 }, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
          <Typography sx={{ fontSize: "13px", color: "rgba(26,10,0,0.4)", fontStyle: "italic" }}>African youth are not waiting. We are building.</Typography>
          <Typography component="a" href="#contact" sx={{ fontSize: "13px", fontWeight: 700, color: "#5C3311", textDecoration: "none", transition: "all 0.2s", "&:hover": { color: "#DDA63A" } }}>Get Involved →</Typography>
        </Box>
      </Container>
    </Box>
  );
}

function WhatWeDo() {
  const [activeModal, setActiveModal] = useState(null);
  const scrollRef = useRef(null);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll, { passive: true });
    window.addEventListener("resize", checkScroll);
    return () => { el.removeEventListener("scroll", checkScroll); window.removeEventListener("resize", checkScroll); };
  }, []);

  const scrollBy = (dir) => scrollRef.current?.scrollBy({ left: dir * 360, behavior: "smooth" });

  return (
    <>
      <Box id="programs" sx={{ bgcolor: "#FFFFFF", py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <SectionHeader label="Explore how our" highlight="Programs" suffix="challenge the status quo" />
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
            <Typography sx={{ fontSize: "11px", color: "rgba(26,10,0,0.4)", display: { xs: "flex", md: "none" }, alignItems: "center", gap: 0.75 }}>Scroll to explore →</Typography>
            <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
              {[{ dir: -1, label: "←", active: canScrollLeft }, { dir: 1, label: "→", active: canScrollRight }].map(({ dir, label, active }) => (
                <Box key={dir} component="button" onClick={() => scrollBy(dir)}
                  sx={{ width: 36, height: 36, borderRadius: "50%", border: "1.5px solid", borderColor: active ? "#5C3311" : "rgba(26,10,0,0.15)", bgcolor: active ? "#5C3311" : "transparent", color: active ? "#fff" : "rgba(26,10,0,0.25)", fontSize: "15px", display: "flex", alignItems: "center", justifyContent: "center", cursor: active ? "pointer" : "default", transition: "all 0.25s ease", "&:hover": active ? { bgcolor: "#DDA63A", borderColor: "#DDA63A" } : {} }}>
                  {label}
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ position: "relative" }}>
            <Box sx={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 80, zIndex: 2, pointerEvents: "none", opacity: canScrollRight ? 1 : 0, transition: "opacity 0.3s ease", display: "flex", alignItems: "center", justifyContent: "flex-end", pr: 1.5 }}>
              <Box sx={{ width: 34, height: 34, borderRadius: "50%", bgcolor: "#5C3311", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "15px", boxShadow: "0 2px 12px rgba(92,51,17,0.35)", animation: "nudge 1.8s ease-in-out infinite", "@keyframes nudge": { "0%,100%": { transform: "translateX(0)" }, "45%": { transform: "translateX(5px)" }, "65%": { transform: "translateX(2px)" } } }}>→</Box>
            </Box>
            <Box ref={scrollRef} sx={{ display: "flex", gap: { xs: "16px", md: "20px" }, overflowX: "auto", pb: 3, scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" }, scrollSnapType: "x mandatory", mx: { xs: -2, md: 0 }, px: { xs: 2, md: 0 } }}>
              {programs.map((prog, index) => (
                <Box component="button" key={prog.title} onClick={() => setActiveModal(prog)}
                  sx={{ flexShrink: 0, width: { xs: "260px", sm: "300px", md: "340px" }, scrollSnapAlign: "start", cursor: "pointer", background: "none", border: "none", p: 0, textAlign: "left" }}>
                  <Box sx={{ overflow: "hidden", borderRadius: "12px", height: { xs: "380px", md: "420px" }, position: "relative", bgcolor: "#e8ddd1", boxShadow: "0 4px 24px rgba(0,0,0,0.08)", "&:hover img": { transform: "scale(1.04)" } }}>
                    {index === 0 ? (
                      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                        <Box sx={{ flex: 1, overflow: "hidden" }}>
                          <Box component="img" src={prog.image} alt={prog.title} sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top", display: "block", transition: "transform 0.65s cubic-bezier(0.25,0.46,0.45,0.94)" }} />
                        </Box>
                        <Box sx={{ flex: 1, overflow: "hidden" }}>
                          <Box component="img" src={prog.image} alt={prog.title} sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center bottom", display: "block", transition: "transform 0.65s cubic-bezier(0.25,0.46,0.45,0.94)" }} />
                        </Box>
                      </Box>
                    ) : (
                      <Box component="img" src={prog.image} alt={prog.title} sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: prog.imagePosition ?? "center 30%", display: "block", transition: "transform 0.65s cubic-bezier(0.25,0.46,0.45,0.94)" }} />
                    )}
                    <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.55) 45%, transparent 100%)", p: "24px 18px 20px", borderRadius: "0 0 12px 12px" }}>
                      <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: { xs: "16px", md: "18px" }, fontWeight: 700, color: "#fff", lineHeight: 1.25, mb: 1.25 }}>{prog.title}</Typography>
                      <Box sx={{ display: "inline-block", px: 2.5, py: 0.6, border: "1.5px solid rgba(255,255,255,0.75)", borderRadius: "6px", fontSize: "10px", fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", color: "#fff", transition: "all 0.2s", "&:hover": { bgcolor: "#5C3311", borderColor: "#5C3311" } }}>Read More</Box>
                    </Box>
                  </Box>
                </Box>
              ))}
              <Box sx={{ flexShrink: 0, width: { xs: "16px", md: "4px" } }} />
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", gap: 0.75, mt: 2 }}>
            {programs.map((_, i) => (
              <Box key={i} onClick={() => scrollRef.current?.scrollTo({ left: i * 360, behavior: "smooth" })} sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "rgba(92,51,17,0.25)", cursor: "pointer", transition: "all 0.2s", "&:hover": { bgcolor: "#5C3311", transform: "scale(1.4)" } }} />
            ))}
          </Box>
        </Container>
      </Box>

      <MissionSection />      {/* ✅ add here */}
       <VideoSection />
      <Box id="events"><EventsSection /></Box>
      {activeModal && <ProgramModal program={activeModal} onClose={() => setActiveModal(null)} />}
    </>
  );
}

function Hero() {
  const [currentIndex, setCurrentIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setCurrentIndex((prev) => (prev + 1) % HERO_IMAGES.length), 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100vh", minHeight: 600, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: "4px", display: "flex", zIndex: 10 }}>
        {SDG_COLORS.map((c, i) => <Box key={i} sx={{ flex: 1, bgcolor: c }} />)}
      </Box>
      {HERO_IMAGES.map((src, index) => (
        <Fade key={src} in={index === currentIndex} timeout={1500}>
          <Box component="img" src={src} alt="" sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", animation: "kenburns 10s ease-in-out infinite alternate", "@keyframes kenburns": { "0%": { transform: "scale(1.0) translateX(0px)" }, "100%": { transform: "scale(1.08) translateX(-12px)" } } }} />
        </Fade>
      ))}
      <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.55) 50%, rgba(0,0,0,0.75) 100%)", zIndex: 1 }} />
      <Box sx={{ position: "absolute", top: 4, left: 0, right: 0, zIndex: 5, px: { xs: 3, md: 6 }, py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontFamily: "'Playfair Display', serif", fontSize: { xs: 16, md: 20 }, fontWeight: 700, color: "#fff", letterSpacing: 0.5 }}>BTSA</Typography>
        <Stack direction="row" spacing={4} sx={{ display: { xs: "none", md: "flex" } }}>
          {["Programs", "About", "Impact", "Partners"].map((link) => (
            <Typography key={link} component="a" href={`#${link.toLowerCase()}`} sx={{ color: "rgba(255,255,255,0.85)", fontSize: 13, fontWeight: 600, letterSpacing: "1px", textTransform: "uppercase", textDecoration: "none", transition: "color 0.2s", "&:hover": { color: "#DDA63A" } }}>{link}</Typography>
          ))}
        </Stack>
        <Button variant="contained" href="#donate" sx={{ bgcolor: "#5C3311", color: "#fff", fontWeight: 700, fontSize: 13, letterSpacing: "1.5px", px: 3, py: 1, borderRadius: "6px", "&:hover": { bgcolor: "#3e220b" }, boxShadow: "none" }}>DONATE</Button>
      </Box>
      <Box sx={{ position: "absolute", inset: 0, zIndex: 2, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", px: { xs: 3, md: 6 } }}>
        <Typography sx={{ color: "#DDA63A", fontSize: { xs: 12, md: 14 }, fontWeight: 700, letterSpacing: "3px", textTransform: "uppercase", mb: 2 }}>Youth-Led · Pan-African · Founded 2025</Typography>
        <Typography component="h1" sx={{ fontFamily: "'Playfair Display', serif", fontSize: { xs: "48px", sm: "72px", md: "96px", lg: "112px" }, fontWeight: 900, color: "#fff", lineHeight: 0.95, letterSpacing: "-1px", textTransform: "uppercase", mb: 1 }}>WE ARE</Typography>
        <Typography component="h1" sx={{ fontFamily: "'Playfair Display', serif", fontSize: { xs: "44px", sm: "66px", md: "88px", lg: "104px" }, fontWeight: 900, color: "#fff", lineHeight: 1, letterSpacing: "-1px", textTransform: "uppercase", mb: 4 }}>
          BUILD TO SHELTER
          <Box component="span" sx={{ color: "#DDA63A", display: "block" }}>AFRICA</Box>
        </Typography>
        <Typography sx={{ fontSize: { xs: "16px", md: "20px" }, color: "rgba(255,255,255,0.88)", fontWeight: 400, maxWidth: 640, lineHeight: 1.65, mb: 5 }}>Building sustainable homes, protecting the planet, and empowering communities across the continent — one shelter at a time.</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <Button variant="contained" size="large" component={Link} to="/volunteer#ongoing-projects" sx={{ bgcolor: "#5C3311", color: "#fff", fontWeight: 700, fontSize: 14, letterSpacing: "2px", px: 5, py: 1.8, borderRadius: "6px", textTransform: "uppercase", "&:hover": { bgcolor: "#3e220b" } }}>GIVE NOW</Button>
          <Button variant="outlined" size="large" component={Link} to="/services" sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.7)", fontWeight: 700, fontSize: 14, letterSpacing: "2px", px: 5, py: 1.8, borderRadius: "6px", textTransform: "uppercase", "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.1)" } }}>OUR WORK</Button>
        </Stack>
      </Box>
      <Box sx={{ position: "absolute", bottom: 32, left: "50%", transform: "translateX(-50%)", zIndex: 3, display: "flex", gap: 1 }}>
        {HERO_IMAGES.map((_, i) => (
          <Box key={i} onClick={() => setCurrentIndex(i)} sx={{ width: i === currentIndex ? 24 : 8, height: 8, borderRadius: "4px", bgcolor: i === currentIndex ? "#DDA63A" : "rgba(255,255,255,0.45)", cursor: "pointer", transition: "all 0.4s ease" }} />
        ))}
      </Box>
    </Box>
  );
}

export default function LandingPage() {
  return (
    <>
      <Hero />
      <WhatWeDo />
      <ImpactSection />
      <Footer />
    </>
  );
}